import {
  all,
  call,
  put,
  takeEvery,
  takeLatest,
  takeLeading,
} from 'redux-saga/effects';
import {actions, ActionType} from './action';
import {
  getContent,
  getContentProfile,
  getContentTicket,
  getEpisode,
  getGiftTicketCount,
  getKeywordDetail,
  getIsLike,
  getPurchase,
  getHasRedDot,
  getLeftCash,
  getContentAdBanner,
  getContentAddedTicketHistory,
  getContentUsedTicketItems,
  getContentTicketDetail,
  getViewData,
  getIsFirstRead,
  getFreeTicketInfo,
} from './apiGet';
import {pAlert} from '../../common/component/PAlert';
import SingleText from '../../common/component/SingleText';
import Text from '../../common/component/Text';
import {IS_CLIENT} from '../../common/constant';
import {Page} from '../../common/constant/page';
import {pToast} from '../../common/container/PToast';
import {getDeviceId} from '../../common/function';
import {selectT} from '../../common/i18n';
import {actions as commonActions} from '../../common/state/action';
import {getNoticeList} from '../../common/state/apiGet';
import {ButtonSet} from '../../common/type/alert';
import callApi, {callApiList} from '../../common/util/api';
import {IPagingParam, makeFetchSaga} from '../../common/util/fetch';
import {formatNumber} from '../../common/util/number';
import {redirectTo, routeBack, routeTo} from '../../common/util/PageRouter';
import {typedSelect} from '../../common/util/saga';
import {sendViewImp} from '../../common/util/tracking';
import {sendGtmEvent} from '../../common/util/tracking/gtm';
import {
  decryptImages,
  getLocalRecentItemsV4,
  getViewerResourceParams,
  setLocalRecentItemsV4,
} from '../../commonSub/function';
import {actions as commonSubActions} from '../../commonSub/state/action';
import {callApiSearchResult} from '../../commonSub/state/callApi';
import {actions as giftActions} from '../../gift/state/action';
import {getRecommendContents} from '../../viewer/state/apiGet';
import {sendPurchaseEvent, sendPurchaseViewImp} from '../common';

function* fetchContent(action: ReturnType<typeof actions.fetchContent>) {
  const {id, res} = action.payload;
  const {success, data} = yield call(callApi, {
    url: `/decorator/v2/decorator/contents/${id}`,
    processApiErrorCallback: error => {
      if (!IS_CLIENT && error.code === 404) {
        redirectTo({pageInfo: '/404', res});
        return true;
      } else if (error.errorType === 'NOT_AVAILABLE_CONTENT') {
        if (IS_CLIENT) {
          setLocalRecentItemsV4(
            getLocalRecentItemsV4().filter(item => item.content.id !== id)
          );
          pAlert({
            title: t => t('contenthome_prohibited_sell_popup'),
          }).then(({okClicked, isDimmedClicked}) => {
            if (okClicked || isDimmedClicked) {
              routeBack();
            }
          });
        } else {
          redirectTo({pageInfo: {page: Page.Home}, res});
        }
        return true;
      } else if (error.code === 400) {
        if (IS_CLIENT) {
          pAlert({title: t => t('error_not_found')}).then(() => routeBack());
        } else {
          redirectTo({res});
        }
        return true;
      } else {
        return false;
      }
    },
  });
  if (success && data) {
    const content = getContent({id, data});
    if (content) {
      // TODO: 현재 collection 정보를 파라미터로 같이 넘긴다. (클라에서 저장된 값)
      yield put(actions.setContent({content}));
    }
  }
}

function* fetchContentProfile(
  action: ReturnType<typeof actions.fetchContentProfile>
) {
  const {id} = action.payload;
  const {success, data} = yield call(callApi, {
    url: `/decorator/v2/decorator/contents/${id}/profile`,
  });

  if (success && data) {
    yield put(actions.setValue('profile', getContentProfile(data)));
  }
}

function* fetchContentAdditional(
  action: ReturnType<typeof actions.fetchContentAdditional>
) {
  const user = yield* typedSelect(state => state.auth.user);
  const {id} = action.payload;
  const isLikeCached = yield* typedSelect(
    state => state.common.contentLikeMap[id]
  );
  const isLogin = !!user;
  const needLikeInfo = isLikeCached === undefined && isLogin;
  const params = [
    {
      url: `/ticket/v2/views/content-home/available-tickets?contentId=${id}`,
    },
    {
      url: '/gift/v1/available-gift-ticket-count',
      params: {contentId: id},
    },
    {
      url: `/section/v1/view/advertisements/content-home?contentId=${id}`,
    },
  ];
  needLikeInfo &&
    params.push({
      url: `/popularity/v2/check-subscription?contentId=${id}`,
    });
  const {data} = yield call(callApiList, params);
  // 일부 API만 성공하더라도 데이터를 넣어줘야 하기 때문에 success = true 조건을 제거
  if (data) {
    const contentTicket = getContentTicket(data[0]);
    const giftTicketCount = getGiftTicketCount(data[1]);
    const contentAdBanner = getContentAdBanner(id, data[2]);

    if (needLikeInfo) {
      const isLike = getIsLike(data[3]);
      if (isLike !== isLikeCached) {
        yield put(commonActions.setContentLike({id, isLike}));
      }
    }
    yield put(
      actions.setContentAdditional({
        contentTicket,
        giftTicketCount,
        contentAdBanner,
      })
    );
  }
}

function* fetchEpisode(
  action: ReturnType<typeof actions.fetchEpisode>,
  params: IPagingParam
) {
  const {contentId, sort} = action.payload;
  const {success, data, meta} = yield call(callApi, {
    url: `/episode/v2/views/content-home/contents/${contentId}/episodes`,
    params: {sort: sort === 'latest' ? '-NO' : 'NO', ...params},
  });
  if (success && data) {
    const t = yield* selectT();
    const user = yield* typedSelect(state => state.auth.user);
    const isLogin = !!user;
    const newEpisode = getEpisode({contentId, data, meta, isLogin, t});
    if (newEpisode?.items.length) {
      yield put(
        actions.addEpisode({
          contentId,
          isFirst: action.isFirst,
          episode: newEpisode,
        })
      );
    }
  }
}

function* fetchReceiveGiftTicket(
  action: ReturnType<typeof actions.fetchReceiveGiftTicket>
) {
  const {id, title: contentTitle, giftTicketCount} = action.payload;
  yield put(actions.receiveGiftTicket({id, giftTicketCount}));
  const {success} = yield call(callApi, {
    url: '/gift/v1/gift-tickets/receive',
    method: 'post',
    data: {contentId: id},
  });
  if (success) {
    const t = yield* selectT();
    const title = (
      <div className="text-center">
        <SingleText className="s17-medium-white light:text-black">
          {contentTitle}
        </SingleText>
        <Text className="s17-medium-white light:text-black">
          {t('giftbox_tab_received_popup', {count: giftTicketCount})}
        </Text>
      </div>
    );
    pAlert({
      title,
      desc: t('contenthome_gift_rental_valid_until_popup'),
    });
    yield put(giftActions.setValue('needRefresh', true));
  } else {
    yield put(
      actions.receiveGiftTicket({id, giftTicketCount, isCancelled: true})
    );
  }
}

function* fetchDeleteReadHistory(
  action: ReturnType<typeof actions.fetchDeleteReadHistory>
) {
  const {id} = action.payload;
  const {success} = yield call(callApi, {
    url: `/episode/v1/read-episodes/contents/${id}`,
    method: 'delete',
  });
  if (success) {
    yield put(actions.setPropertyValues('episodeMap', {[id]: undefined}));
    const t = yield* selectT();
    pToast(t('contenthome_sidemenu_clearhistory_deleted'));
  }
}

function* fetchPurchaseInfo(
  action: ReturnType<typeof actions.fetchPurchaseInfo>
) {
  const t = yield* selectT();
  const {id} = action.payload;

  // TODO: API 요청하는 형식 확인하기
  const {success, data} = yield call(callApi, {
    url: 'ticket/v1/views/ticket-purchase',
    params: {contentId: id},
  });

  if (success) {
    const purchase = getPurchase(data, t);
    yield put(actions.setValue('purchase', purchase));
    yield put(commonActions.setValue('userTotalCash', getLeftCash(data)));
  }
}

function* fetchPurchaseVoucher(
  action: ReturnType<typeof actions.fetchPurchaseVoucher>
) {
  const {
    serverData,
    isBuyAtOnce,
    ticketId,
    price,
    bundleCount,
    sid,
    id,
    isBuy,
    contentTitle,
    isDiscount,
    isShowLatestInfo,
  } = action.payload;

  if (isShowLatestInfo) {
    const params: ReturnType<typeof actions.fetchPurchaseInfo> = {
      type: ActionType.FetchPurchaseInfo,
      payload: {id, sid},
    };

    yield call(fetchPurchaseInfo, params);
  }

  const purchaseVoucher = yield* typedSelect(
    state => state.content.purchaseVoucher
  );

  const leftCash = yield* typedSelect(state => state.common.userTotalCash);

  const selectedEpisode = yield* typedSelect(
    state => state.content.selectedEpisode
  );

  const {success} = yield call(callApi, {
    url: 'ticket/v1/purchases',
    method: 'post',
    data: {
      ticketPrice: price,
      contentId: id,
      included: isBuyAtOnce
        ? serverData
        : [
            {
              quantity: purchaseVoucher.count,
              ticketPackageId: ticketId,
            },
          ],
    },
  });

  if (success && leftCash) {
    sendPurchaseEvent({
      actionName: 'ticket_purchase',
      action: 'complete',
      kind: 'Purchase',
      customProps: {
        page_content_id: id,
        ticket_item_id: ticketId,
        event_layer: 'popup',
        ticket_type: isBuy ? 'possession' : 'rental',
        is_discount: isDiscount ? 'Y' : 'N',
        popup_type: 'ticketpurchase_complete',
      },
      ecommerceContents: {
        price_amt: purchaseVoucher.price,
        quantity: purchaseVoucher.totalCount,
      },
    });
    sendPurchaseViewImp({
      customProps: {
        page_content_id: id,
        ticket_item_id: ticketId,
        event_layer: 'popup',
        ticket_type: isBuy ? 'possession' : 'rental',
        is_discount: isDiscount ? 'Y' : 'N',
        popup_type: 'ticketpurchase_complete',
      },
      viewImpContents: {
        name: 'popup',
      },
    });

    const {okClicked, isDimmedClicked} = yield call(pAlert, {
      buttonSet: ButtonSet.Confirm,
      labelConfirm: t => t('common_confirm'),
      title: t =>
        t(
          isBuy
            ? 'ticket_purchase_successful_possession'
            : 'ticket_purchase_successful_rental',
          {
            value: isBuyAtOnce ? bundleCount : purchaseVoucher.totalCount,
          }
        ),
      desc: t =>
        t('ticket_purchase_successful_balance_cash', {
          value: formatNumber(leftCash - purchaseVoucher.price),
        }),
    });

    yield call(sendGtmEvent, 'EVENT_NAME_TICKETPURCHASED', {
      contentTitle,
      totalTicket: purchaseVoucher.totalCount,
    });

    if (okClicked || isDimmedClicked) {
      sendPurchaseEvent({
        actionName: 'popup_ok',
        action: 'click',
        customProps: {
          page_content_id: id,
          ticket_item_id: ticketId,
          event_layer: isDimmedClicked ? 'dimmed' : 'popup',
          ticket_type: isBuy ? 'possession' : 'rental',
          is_discount: isDiscount ? 'Y' : 'N',
          purchase_cnt: bundleCount,
          popup_type: 'ticketpurchase_complete',
        },
      });

      yield put(actions.fetchPurchaseInfo({id, sid}));

      if (selectedEpisode) {
        yield put(
          commonSubActions.fetchUseTicket({
            contentId: id,
            contentSid: sid,
            episodeId: selectedEpisode.episodeId,
            episodeSid: selectedEpisode.episodeSid,
            isReadAgainAfterExpire: selectedEpisode.isTryReadAgain,
            ticketType: isBuy ? 'possession' : 'rental',
            // 구매 페이지인 경우, 유료 회차기 때문에 isFree = false 값을 전달한다.
            isFree: false,
          })
        );
      }
    }
  }
}

function* fetchKeywordContents(
  action: ReturnType<typeof actions.fetchKeywordContents>,
  params: IPagingParam
) {
  const {success, data} = yield call(
    callApiSearchResult,
    action.payload,
    params
  );
  if (success && data) {
    const keywordDetail = getKeywordDetail(data);
    yield put(actions.AddKeywordContents(keywordDetail, action.isFirst));
  }
}

function* fetchHasRedDot(action: ReturnType<typeof actions.fetchHasRedDot>) {
  const {contentId} = action.payload;
  const {success, data} = yield call(callApi, {
    url: `/notification/v1/content-notice/${contentId}/last-read-time`,
  });

  if (success) {
    yield put(actions.setValue('hasRedDot', getHasRedDot(data)));
  }
}

function* fetchContentTicketHistory(
  action: ReturnType<typeof actions.fetchContentTicketHistory>,
  params: IPagingParam
) {
  const {tab, id} = action.payload;
  const isAddedTab = tab === 'added';
  const pagingProp = isAddedTab ? 'cursor' : 'offset';
  const pagingValue = isAddedTab ? params.cursor : params.offset;
  const {success, data} = yield call(callApi, {
    url: isAddedTab
      ? '/ticket/v1/views/ticket-charged-summary'
      : '/history/v1/ticket-used',
    params: {
      contentId: id,
      [pagingProp]: pagingValue,
      limit: params.limit,
    },
  });

  if (success && data) {
    const t = yield* selectT();
    yield put(
      actions.addContentTicketContents(
        tab === 'added'
          ? {
              tab,
              ...getContentAddedTicketHistory(data, t),
            }
          : {
              tab,
              contents: getContentUsedTicketItems(data, t),
            },
        action.isFirst
      )
    );
  }
}

function* fetchContentTicketDetail(
  action: ReturnType<typeof actions.fetchContentTicketDetail>
) {
  const {ticketId} = action.payload;
  const {success, data} = yield call(callApi, {
    url: `/ticket/v1/purchases/${ticketId}`,
  });

  if (success && data) {
    yield put(
      actions.setValue('contentTicketDetail', getContentTicketDetail(data))
    );
  }
}

function* fetchContentTicketRefund(
  action: ReturnType<typeof actions.fetchContentTicketRefund>
) {
  const {id, ticketId, sid} = action.payload;
  const {success} = yield call(callApi, {
    method: 'POST',
    url: `/ticket/v1/purchases/${ticketId}/refund`,
  });
  if (success) {
    const t = yield* selectT();
    pToast(t('contenthome_tickethistory_refund_snackbar'));
    routeTo({page: Page.Content, id, sid, tab: 'ticket'});
  }
}

function* fetchIsFirstRead(action: ReturnType<typeof actions.fetchHasRedDot>) {
  const {contentId} = action.payload;
  const {success, data} = yield call(callApi, {
    url: '/episode/v1/views/is-first-read',
    params: {
      contentId,
    },
  });
  if (success) {
    yield put(actions.setValue('isFirstRead', getIsFirstRead(data)));
  }
}

function* fetchViewData(action: ReturnType<typeof actions.fetchViewData>) {
  const {contentId, episodeId} = action.payload;
  const {type, nonce, timestamp} = yield call(getViewerResourceParams);
  const {success, data} = yield call(callApi, {
    url: '/episode/v1/views/continue',
    method: 'post',
    data: {
      contentId,
      episodeId,
      type,
      timestamp,
      nonce,
    },
  });
  if (success && data) {
    const t = yield* selectT();
    const viewData = getViewData({data, nonce, timestamp, t});
    yield put(actions.setValue('viewData', viewData));
    yield call(decryptImages, {
      initialPage: 1,
      episodeId: viewData.episode.id,
      onFetchImgFinishedAction: ActionType.OnFetchImageFinished,
      encUrls: viewData.images.map(img => img.encUrl),
      decryptInfo: viewData.decryptInfo,
      actions,
    });
  }
}

function* fetchEpisodeRecommendation(
  action: ReturnType<typeof actions.fetchEpisodeRecommendation>
) {
  const {contentId} = action.payload;
  const {success, data} = yield call(callApi, {
    url: '/decorator/v2/view/recommendations/most-similar',
    params: {contentId},
  });
  if (success) {
    const recommendContents = getRecommendContents(data);
    yield put(actions.setValue('recommendContents', recommendContents));
  }
}

function* fetchRecentNotice(
  action: ReturnType<typeof actions.fetchRecentNotice>
) {
  const {contentId} = action.payload;
  const {success, data} = yield call(callApi, {
    url: `/notification/v1/content-notice/${contentId}?mainNotice=true`,
  });
  if (success) {
    const t = yield* selectT();
    const recentNotice = getNoticeList(t, data)[0];
    if (recentNotice) {
      yield put(actions.setValue('recentNotice', recentNotice));
    }
  }
}

function* fetchReadFirstEpisode(
  action: ReturnType<typeof actions.fetchReadFirstEpisode>
) {
  const {contentId, episodeId} = action.payload;
  yield call(callApi, {
    url: `/episode/v1/preview-episodes/contents/${contentId}/episodes/${episodeId}`,
    method: 'post',
    data: {
      webAppId: getDeviceId(),
    },
  });
}

function* fetchFreeTicket(action: ReturnType<typeof actions.fetchFreeTicket>) {
  const t = yield* selectT();
  const {contentId, contentTitle, tab} = action.payload;
  const {success, data} = yield call(callApi, {
    method: 'POST',
    url: '/gift/v1/check-and-receive-free-tickets',
    data: {contentId},
  });

  if (success && data) {
    const freeTicketInfo = getFreeTicketInfo(data);

    if (freeTicketInfo.showFreeTicketPopup) {
      for (const freeTicket of freeTicketInfo.freeTickets) {
        sendViewImp({
          section: 'content_home',
          page: 'content_home',
          customProps: {
            page_content_id: contentId,
            event_layer: 'popup',
            popup_type: 'helix_received',
            ticket_id: freeTicket.ticketId,
          },
        });

        yield call(pAlert, {
          title: (
            <div className="text-center">
              <SingleText className="s17-medium-white light:text-black">
                {contentTitle}
              </SingleText>
              <Text className="s17-medium-white light:text-black">
                {t('contenthome_gift_rental_popup', {
                  count: freeTicket.ticketCount,
                })}
              </Text>
            </div>
          ),
          desc: (
            <Text className="s13-regular-grey08">
              {t('contenthome_expiry_specific_date', {
                value: freeTicket.expiredDate,
              })}
            </Text>
          ),
        });
      }

      yield put(actions.fetchContentAdditional({id: contentId}));

      // 헬릭스 푸시받은 작품홈을 이용권 탭으로 다이렉트 진입했을 때만 호출
      if (tab === 'ticket') {
        yield put(
          actions.fetchContentTicketHistory({tab: 'added', id: contentId}, true)
        );
        yield put(
          actions.fetchContentTicketHistory({tab: 'used', id: contentId}, true)
        );
      }
    }
  }
}

export default function* saga() {
  yield all([
    takeEvery(
      ActionType.FetchContent,
      makeFetchSaga({fetchSaga: fetchContent, canCache: true})
    ),
    takeLeading(
      ActionType.FetchContentProfile,
      makeFetchSaga({fetchSaga: fetchContentProfile, canCache: true})
    ),
    takeLeading(
      ActionType.FetchContentAdditional,
      makeFetchSaga({fetchSaga: fetchContentAdditional, canCache: false})
    ),
    takeEvery(
      ActionType.FetchEpisode,
      makeFetchSaga({fetchSaga: fetchEpisode, canCache: false})
    ),
    takeLeading(
      ActionType.FetchReceiveGiftTicket,
      makeFetchSaga({fetchSaga: fetchReceiveGiftTicket, canCache: false})
    ),
    takeLeading(
      ActionType.FetchDeleteReadHistory,
      makeFetchSaga({fetchSaga: fetchDeleteReadHistory, canCache: false})
    ),
    takeLeading(
      ActionType.FetchPurchaseInfo,
      makeFetchSaga({fetchSaga: fetchPurchaseInfo, canCache: false})
    ),
    takeLeading(
      ActionType.FetchPurchaseVoucher,
      makeFetchSaga({fetchSaga: fetchPurchaseVoucher, canCache: false})
    ),
    takeLeading(
      ActionType.FetchKeywordContents,
      makeFetchSaga({fetchSaga: fetchKeywordContents, canCache: true})
    ),
    takeLeading(
      ActionType.FetchHasRedDot,
      makeFetchSaga({fetchSaga: fetchHasRedDot, canCache: false})
    ),
    takeEvery(
      ActionType.FetchContentTicketHistory,
      makeFetchSaga({fetchSaga: fetchContentTicketHistory, canCache: false})
    ),
    takeLeading(
      ActionType.FetchContentTicketDetail,
      makeFetchSaga({fetchSaga: fetchContentTicketDetail, canCache: false})
    ),
    takeLeading(
      ActionType.FetchContentTicketRefund,
      makeFetchSaga({fetchSaga: fetchContentTicketRefund, canCache: false})
    ),
    takeLeading(
      ActionType.FetchIsFirstRead,
      makeFetchSaga({fetchSaga: fetchIsFirstRead, canCache: false})
    ),
    takeLatest(
      ActionType.FetchViewData,
      makeFetchSaga({fetchSaga: fetchViewData, canCache: false})
    ),
    takeLeading(
      ActionType.FetchEpisodeRecommendation,
      makeFetchSaga({fetchSaga: fetchEpisodeRecommendation, canCache: true})
    ),
    takeLeading(
      ActionType.FetchRecentNotice,
      makeFetchSaga({fetchSaga: fetchRecentNotice, canCache: true})
    ),
    takeLeading(
      ActionType.FetchReadFirstEpisode,
      makeFetchSaga({fetchSaga: fetchReadFirstEpisode, canCache: false})
    ),
    takeLeading(
      ActionType.FetchFreeTicket,
      makeFetchSaga({fetchSaga: fetchFreeTicket, canCache: false})
    ),
  ]);
}
